import React from 'react';
import { fmtHM } from '../lib/format.js';
import { Icon } from './Icon.jsx';
import { StatusPill } from './StatusPill.jsx';

/**
 * Team Monthly Health — aggregate roll-up of an approver's reports for one
 * month, sliced three ways: overall (everyone they can access), by
 * department, and by country.
 *
 * It consumes the *same* computed team rows that drive the per-person detail
 * table (the `rows` from `GET /api/team`), so the two views can never
 * disagree on a number. Each row carries: reportedMins, expectedMins,
 * missingMins, overtimeMins, status (ok|warning|alert), department, team
 * (country: PH|PL), and approval.state.
 */

// Health tones — warm→green family, deliberately a touch more saturated than
// the status pills so the bars read clearly at small sizes.
export const HEALTH_TONE = {
  ok: { bar: '#2BB371', dot: '#1F8A5B', soft: 'rgba(31,138,91,0.14)' },
  warning: { bar: '#E0913D', dot: '#B57651', soft: 'rgba(196,124,42,0.16)' },
  alert: { bar: '#D24428', dot: '#C4320A', soft: 'rgba(196,50,10,0.14)' },
};

// Worst-first ordering for the grouped cards: alert before warning before ok.
const STATUS_ORDER = { alert: 0, warning: 1, ok: 2 };
const STATUS_LABEL = { ok: 'On track', warning: 'Warning', alert: 'Alert' };
const FLAG_EMOJI = { PH: '🇵🇭', PL: '🇵🇱' };
const COUNTRY_NAME = { PH: 'Philippines', PL: 'Poland' };

/**
 * Roll a set of computed team rows into one health summary.
 *
 * Group status is the *worst member state* — alert if any report is in
 * alert, else warning if any is warning, else ok. It is deliberately NOT
 * derived from the summed reported/expected ratio: an approver must never
 * miss one at-risk person hiding inside an otherwise-healthy total.
 */
export function aggregateRows(rs) {
  const reported = rs.reduce((s, r) => s + (r.reportedMins || 0), 0);
  const expected = rs.reduce((s, r) => s + (r.expectedMins || 0), 0);
  const ok = rs.filter((r) => r.status === 'ok').length;
  const warning = rs.filter((r) => r.status === 'warning').length;
  const alert = rs.filter((r) => r.status === 'alert').length;
  const approved = rs.filter((r) => r.approval?.state === 'approved').length;
  const missing = rs.reduce((s, r) => s + (r.missingMins || 0), 0);
  const overtime = rs.reduce((s, r) => s + (r.overtimeMins || 0), 0);
  const pct = expected > 0 ? Math.round((reported / expected) * 100) : 0;
  const status = alert > 0 ? 'alert' : warning > 0 ? 'warning' : 'ok';
  return {
    count: rs.length,
    reported,
    expected,
    ok,
    warning,
    alert,
    approved,
    missing,
    overtime,
    pct,
    status,
  };
}

/**
 * Build the three lenses from the report rows. Pure, so it's unit-tested
 * directly.
 *
 * - overall   — aggregate of every accessible report.
 * - byDept    — one group per department present in the set, sorted
 *               worst-first (alert→warning→ok), then headcount desc, then
 *               name for a stable order.
 * - byCountry — one group per country present (PH, PL), worst-first.
 */
export function buildGroups(rows) {
  const overall = aggregateRows(rows);

  const deptKeys = Array.from(
    new Set(rows.map((r) => r.department).filter(Boolean)),
  );
  const byDept = deptKeys
    .map((d) => ({
      key: d,
      label: d,
      rows: rows.filter((r) => r.department === d),
    }))
    .map((g) => ({ ...g, agg: aggregateRows(g.rows) }))
    .sort(
      (a, b) =>
        STATUS_ORDER[a.agg.status] - STATUS_ORDER[b.agg.status] ||
        b.agg.count - a.agg.count ||
        a.label.localeCompare(b.label),
    );

  const countryKeys = ['PH', 'PL'].filter((c) =>
    rows.some((r) => r.team === c),
  );
  const byCountry = countryKeys
    .map((c) => ({
      key: c,
      label: COUNTRY_NAME[c] || c,
      rows: rows.filter((r) => r.team === c),
    }))
    .map((g) => ({ ...g, agg: aggregateRows(g.rows) }))
    .sort(
      (a, b) =>
        STATUS_ORDER[a.agg.status] - STATUS_ORDER[b.agg.status] ||
        b.agg.count - a.agg.count ||
        a.label.localeCompare(b.label),
    );

  return { overall, byDept, byCountry };
}

// Stacked head-count bar: ok / warning / alert proportions.
function StatusMixBar({ agg, height = 8 }) {
  const segs = [
    { k: 'ok', n: agg.ok },
    { k: 'warning', n: agg.warning },
    { k: 'alert', n: agg.alert },
  ].filter((s) => s.n > 0);
  return (
    <div
      style={{
        display: 'flex',
        height,
        borderRadius: 999,
        overflow: 'hidden',
        background: 'var(--sp-bg-muted)',
        gap: 2,
      }}
    >
      {segs.map((s) => (
        <div
          key={s.k}
          title={`${s.n} ${s.k === 'ok' ? 'on track' : s.k}`}
          style={{ flex: s.n, background: HEALTH_TONE[s.k].bar, minWidth: 3 }}
        />
      ))}
    </div>
  );
}

function MixLegend({ agg, compact = false }) {
  const items = [
    { k: 'ok', label: 'On track', n: agg.ok },
    { k: 'warning', label: 'Warning', n: agg.warning },
    { k: 'alert', label: 'Alert', n: agg.alert },
  ].filter((i) => (compact ? i.n > 0 : true));
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 10 : 16,
        flexWrap: 'wrap',
      }}
    >
      {items.map((i) => (
        <span
          key={i.k}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: compact ? 12 : 13,
            color: 'var(--sp-fg-2)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: HEALTH_TONE[i.k].dot,
              flex: 'none',
            }}
          />
          <strong style={{ color: 'var(--sp-fg-1)', fontWeight: 700 }}>
            {i.n}
          </strong>
          <span style={{ color: 'var(--sp-fg-3)' }}>{i.label}</span>
        </span>
      ))}
    </div>
  );
}

// Explicit on-track / warning / alert tallies — shown for every group row.
// Zeros stay visible but muted so the three lanes always read at a glance;
// fixed-width lanes + tabular-nums keep the numbers aligned across rows.
function StatusCounts({ agg }) {
  const items = [
    { k: 'ok', label: 'On track', n: agg.ok },
    { k: 'warning', label: 'Warning', n: agg.warning },
    { k: 'alert', label: 'Alert', n: agg.alert },
  ];
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 26 }}>
      {items.map((i) => (
        <div
          key={i.k}
          style={{
            width: 50,
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: 999,
                flex: 'none',
                background:
                  i.n > 0 ? HEALTH_TONE[i.k].dot : 'var(--sp-border-strong)',
              }}
            />
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
                color: i.n > 0 ? 'var(--sp-fg-1)' : 'var(--sp-fg-4)',
              }}
            >
              {i.n}
            </span>
          </span>
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              color: 'var(--sp-fg-3)',
              whiteSpace: 'nowrap',
            }}
          >
            {i.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// Reported-vs-expected progress bar, tinted by aggregate status.
function HealthBar({ agg }) {
  const tone = HEALTH_TONE[agg.status];
  return (
    <div
      style={{
        height: 8,
        borderRadius: 999,
        background: 'var(--sp-bg-muted)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: Math.min(100, agg.pct) + '%',
          height: '100%',
          background: tone.bar,
          borderRadius: 999,
          transition: 'width .4s ease',
        }}
      />
    </div>
  );
}

// One grouping row inside a GroupCard. Rows are summaries — there is no
// drill-down from a department/country (per-person drill-in lives in the
// "By person" table), so no onClick / hover affordance.
function HealthGroupRow({ leading, label, sub, agg, isLast }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1.4fr 1.9fr 1.25fr 0.85fr',
        alignItems: 'center',
        gap: 18,
        padding: '15px 20px',
        borderBottom: isLast ? 0 : '1px solid var(--sp-border-soft)',
      }}
    >
      {/* Identity */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}
      >
        {leading}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--sp-fg-1)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: 11, color: 'var(--sp-fg-3)' }}>{sub}</div>
        </div>
      </div>
      {/* Reported / expected + progress */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--sp-fg-1)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {fmtHM(agg.reported)}
          </span>
          <span
            style={{
              fontSize: 12,
              color: 'var(--sp-fg-3)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            / {fmtHM(agg.expected)}
          </span>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11,
              color: 'var(--sp-fg-3)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {agg.pct}%
          </span>
        </div>
        <HealthBar agg={agg} />
      </div>
      {/* Status counts */}
      <StatusCounts agg={agg} />
      {/* Headline pill */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <StatusPill kind={agg.status} label={STATUS_LABEL[agg.status]} size="sm" />
      </div>
    </div>
  );
}

function GroupCard({ title, icon, hint, children }) {
  return (
    <div className="card" style={{ padding: 0, borderRadius: 14, overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '14px 20px',
          borderBottom: '1px solid var(--sp-border)',
        }}
      >
        <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
          <Icon name={icon} size={15} />
        </span>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--sp-fg-1)' }}>
          {title}
        </h3>
        {hint && (
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--sp-fg-3)' }}>
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function HeroStat({ label, value, hint, accent }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--sp-fg-3)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.01em',
          color: accent || 'var(--sp-fg-1)',
          fontVariantNumeric: 'tabular-nums',
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {hint && (
        <div style={{ fontSize: 11, color: 'var(--sp-fg-3)', marginTop: 1 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

/**
 * The Monthly health overview. `rows` are the per-report rows for the
 * selected month — TRACKED users only; the caller (TeamView) has already
 * partitioned out excluded users and passes their tally as `exemptCount`
 * so the hero can disclose how many were left out of every figure.
 * `monthLabel` is e.g. "April 2026".
 */
export function TeamHealth({ rows = [], exemptCount = 0, monthLabel = '' }) {
  const { overall, byDept, byCountry } = buildGroups(rows);
  const atRisk = overall.warning + overall.alert;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Hero — overall health across the people this approver can access */}
      <div className="card" style={{ padding: '24px 28px', borderRadius: 18 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.6fr 1fr',
            gap: 28,
            alignItems: 'center',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--sp-fg-3)',
                marginBottom: 6,
              }}
            >
              Monthly health · across your {overall.count}{' '}
              {overall.count === 1 ? 'report' : 'reports'}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  fontSize: 44,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'var(--sp-fg-1)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {overall.ok}
                <span style={{ color: 'var(--sp-fg-3)', fontWeight: 600 }}>
                  /{overall.count}
                </span>
              </span>
              <span style={{ fontSize: 16, color: 'var(--sp-fg-2)', fontWeight: 600 }}>
                on track in {monthLabel}
              </span>
            </div>
            <div style={{ marginTop: 16, marginBottom: 12 }}>
              <StatusMixBar agg={overall} height={12} />
            </div>
            <MixLegend agg={overall} />
            {/* v2.6 — quiet disclosure that excluded users were left out of
                every figure above. Only shown when ≥1 is in scope. */}
            {exemptCount > 0 && (
              <div
                style={{
                  marginTop: 12,
                  fontSize: 12,
                  color: 'var(--sp-fg-3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: 'var(--sp-fg-4)',
                    flex: 'none',
                  }}
                />
                Excludes {exemptCount}{' '}
                {exemptCount === 1 ? 'user' : 'users'} not required to log time
              </div>
            )}
          </div>

          <div
            style={{
              borderLeft: '1px solid var(--sp-border)',
              paddingLeft: 24,
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'var(--sp-fg-3)',
                }}
              >
                Reported
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: 'var(--sp-fg-1)',
                  fontVariantNumeric: 'tabular-nums',
                  marginTop: 2,
                }}
              >
                {fmtHM(overall.reported)}{' '}
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sp-fg-3)' }}>
                  / {fmtHM(overall.expected)}
                </span>
              </div>
              <div style={{ marginTop: 8 }}>
                <HealthBar agg={overall} />
              </div>
            </div>
            <HeroStat
              label="Need attention"
              value={atRisk}
              hint={atRisk === 1 ? 'report behind target' : 'reports behind target'}
              accent={atRisk > 0 ? HEALTH_TONE.alert.dot : undefined}
            />
            <HeroStat
              label="Approved"
              value={`${overall.approved} / ${overall.count}`}
              hint="months locked"
            />
          </div>
        </div>
      </div>

      {/* By department */}
      <GroupCard
        title="By department"
        icon="flow"
        hint={`${byDept.length} department${byDept.length === 1 ? '' : 's'}`}
      >
        {byDept.map((g, i) => (
          <HealthGroupRow
            key={g.key}
            label={g.label}
            sub={`${g.agg.count} ${g.agg.count === 1 ? 'person' : 'people'}`}
            agg={g.agg}
            isLast={i === byDept.length - 1}
            leading={
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: HEALTH_TONE[g.agg.status].soft,
                  color: HEALTH_TONE[g.agg.status].dot,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 'none',
                }}
              >
                <Icon name="flow" size={14} />
              </span>
            }
          />
        ))}
      </GroupCard>

      {/* By country */}
      <GroupCard
        title="By country"
        icon="users"
        hint={`${byCountry.length} ${byCountry.length === 1 ? 'country' : 'countries'}`}
      >
        {byCountry.map((g, i) => (
          <HealthGroupRow
            key={g.key}
            label={g.label}
            sub={`${g.agg.count} ${g.agg.count === 1 ? 'person' : 'people'}`}
            agg={g.agg}
            isLast={i === byCountry.length - 1}
            leading={
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: 'var(--sp-bg-muted)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  flex: 'none',
                }}
                aria-hidden="true"
              >
                {FLAG_EMOJI[g.key]}
              </span>
            }
          />
        ))}
      </GroupCard>
    </div>
  );
}
